import React, { useState, useEffect, useMemo } from "react";
import {
    History,
    Search,
    FileSpreadsheet,
    Loader2,
    Truck,
    PackageCheck,
    Calendar
} from "lucide-react";
import { format, subDays } from "date-fns";
import { fetchGoodsReceiptHistory, fetchGoodsReceiptHistoryCSV } from "../apiHelper";
import { useToast } from "../hooks/useToast";
import TrialModal from "../components/ui/TrialModal";

const GoodsReceiptHistory = () => {
    const { showToast } = useToast();

    const [startDate, setStartDate] = useState(format(subDays(new Date(), 7), "yyyy-MM-dd"));
    const [endDate, setEndDate] = useState(format(new Date(), "yyyy-MM-dd"));
    const [loading, setLoading] = useState(false);
    const [historyData, setHistoryData] = useState([]);
    const [searchTerm, setSearchTerm] = useState("");
    const [exporting, setExporting] = useState(false);

    const [showTrialModal, setShowTrialModal] = React.useState(true); // or use logic like localStorage to show once

    useEffect(() => {
        const loadHistory = async () => {
            setLoading(true);
            try {
                const payload = { start_date: startDate, end_date: endDate };
                // console.log(`GOODS RECEIPT PAYLOAD:`, payload);
                const data = await fetchGoodsReceiptHistory(payload);
                setHistoryData(Array.isArray(data) ? data : []);
            } catch (err) {
                showToast({ message: "Failed to load receipt history.", show: true, toastType: 2 });
            } finally {
                setLoading(false);
            }
        };
        loadHistory();
    }, [startDate, endDate, showToast]);

    const filteredData = useMemo(() => {
        return historyData.filter(item =>
            item.productname.toLowerCase().includes(searchTerm.toLowerCase()) ||
            item.vendorname?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            item.store_name.toLowerCase().includes(searchTerm.toLowerCase())
        );
    }, [historyData, searchTerm]);

    const handleExport = async () => {
        setExporting(true);
        try {
            const payload = { start_date: startDate, end_date: endDate };

            // 1. Fetch the raw CSV string from the API
            const csvContent = await fetchGoodsReceiptHistoryCSV(payload);

            // 2. Create the Blob from the response
            const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });

            // 3. Create a temporary URL for the Blob
            const url = window.URL.createObjectURL(blob);

            // 4. Create an invisible anchor tag and trigger the download
            const link = document.createElement('a');
            link.href = url;

            // Construct the filename
            const filename = `Goods_Receipt_History_${startDate}_to_${endDate}.csv`;
            link.setAttribute('download', filename);

            // Append, click, and clean up
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);

            // Revoke the URL to free up memory
            window.URL.revokeObjectURL(url);

            showToast("Export completed successfully.", "success");
        } catch (err) {
            console.error("Export Error:", err);
            showToast("Failed to export CSV.", "error");
        } finally {
            setExporting(false);
        }
    };

    const formatCurrency = (val) =>
        new Intl.NumberFormat('en-NG', { style: 'currency', currency: 'NGN' }).format(val);

    return (
        <div className="min-h-screen bg-gray-50 p-3 md:p-6">
            {/* Header - Stacked on Mobile, Row on Desktop */}
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-6 gap-4">
                <div>
                    <h1 className="text-xl md:text-2xl font-bold text-gray-900 flex items-center gap-2">
                        <History className="text-blue-600" size={24} />
                        Receipt History
                    </h1>
                    <p className="text-xs md:text-sm text-gray-500 font-medium">Organizational Audit Trail</p>
                </div>
                <button
                    onClick={handleExport}
                    disabled={exporting || loading}
                    className="w-full md:w-auto flex items-center justify-center gap-2 px-4 py-2.5 bg-green-600 text-white rounded-lg hover:bg-green-700 shadow-sm text-sm font-bold disabled:opacity-50 disabled:cursor-not-allowed"
                >
                    {exporting ? (
                        <Loader2 size={18} className="animate-spin" />
                    ) : (
                        <FileSpreadsheet size={18} />
                    )}
                    {exporting ? "Exporting..." : "Export"}
                </button>
            </div>

            {/* Filter Bar - Responsive Grid */}
            <div className="bg-white p-4 rounded-xl shadow-sm border border-gray-200 mb-6 space-y-4">
                <div className="w-full">
                    <label className="text-[10px] font-bold text-gray-400 uppercase mb-1 block">Search Audit Logs</label>
                    <div className="relative">
                        <Search className="absolute left-3 top-2.5 text-gray-400" size={18} />
                        <input
                            type="text"
                            placeholder="Product, vendor, or store..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="w-full pl-10 pr-4 py-2 bg-gray-50 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none text-sm"
                        />
                    </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                    <div>
                        <label className="text-[10px] font-bold text-gray-400 uppercase mb-1 block">Date From</label>
                        <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} className="w-full border border-gray-200 bg-gray-50 rounded-lg p-2 text-xs md:text-sm" />
                    </div>
                    <div>
                        <label className="text-[10px] font-bold text-gray-400 uppercase mb-1 block">Date To</label>
                        <input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} className="w-full border border-gray-200 bg-gray-50 rounded-lg p-2 text-xs md:text-sm" />
                    </div>
                </div>
            </div>

            {loading ? (
                <div className="py-20 flex flex-col items-center justify-center gap-4">
                    <Loader2 className="animate-spin text-blue-600" size={40} />
                    <span className="text-gray-500 text-sm">Synchronizing data...</span>
                </div>
            ) : (
                <>
                    {/* Desktop View (md+) */}
                    <div className="hidden md:block bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                        <table className="w-full text-left border-collapse">
                            <thead className="bg-gray-50 border-b border-gray-200">
                                <tr>
                                    <th className="px-6 py-4 text-[10px] font-bold text-gray-400 uppercase">Timestamp</th>
                                    <th className="px-6 py-4 text-[10px] font-bold text-gray-400 uppercase">Product Details</th>
                                    <th className="px-6 py-4 text-[10px] font-bold text-gray-400 uppercase">Location / Vendor</th>
                                    <th className="px-6 py-4 text-[10px] font-bold text-gray-400 uppercase text-right">Qty Ordered</th>
                                    <th className="px-6 py-4 text-[10px] font-bold text-gray-400 uppercase text-right">Qty Received</th>
                                    <th className="px-6 py-4 text-[10px] font-bold text-gray-400 uppercase text-right">Financials</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100">
                                {filteredData.map((row) => (
                                    <tr key={row.goodsreceipt_id} className="hover:bg-blue-50/30 transition-colors">
                                        <td className="px-6 py-4 text-xs text-gray-500">
                                            {format(new Date(row.receiptdate), "dd/MM/yy")}<br />
                                            <span className="text-[10px] opacity-60">{format(new Date(row.receiptdate), "HH:mm")}</span>
                                        </td>
                                        <td className="px-6 py-4">
                                            <div className="font-bold text-gray-900">{row.productname}</div>
                                            <div className="text-[10px] text-gray-400 italic font-medium">{row.uom_full_name}</div>
                                        </td>
                                        <td className="px-6 py-4 text-sm">
                                            <div className="text-gray-700 font-semibold">{row.store_name}</div>
                                            <div className="text-xs text-gray-400 font-medium">{row.vendorname || "Ad-Hoc"}</div>
                                        </td>
                                        <td className="px-6 py-4 text-right text-sm text-gray-400 italic">
                                            {row.po_qty_ordered?.toLocaleString() || "—"}
                                        </td>
                                        <td className="px-6 py-4 text-right">
                                            <span className={`font-mono font-bold text-sm ${row.is_short_shipment ? 'text-orange-600' : 'text-gray-900'}`}>
                                                {row.qtyreceived.toLocaleString()}
                                            </span>
                                            <span className="ml-1 text-[10px] text-gray-400 font-bold uppercase">{row.quantitymeasure}</span>
                                        </td>
                                        <td className="px-6 py-4 text-right">
                                            <div className="font-bold text-blue-700">{formatCurrency(row.total_value)}</div>
                                            <div className="text-[10px] text-gray-400">@{formatCurrency(row.unit_cost)}</div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    {/* Mobile View (<md) */}
                    <div className="md:hidden flex flex-col gap-3">
                        {filteredData.map((row) => (
                            <div key={row.goodsreceipt_id} className="bg-white p-4 rounded-xl border border-gray-200 shadow-sm active:scale-[0.98] transition-transform">
                                <div className="flex justify-between items-start mb-3">
                                    <div className="max-w-[70%]">
                                        <h3 className="font-bold text-gray-900 text-sm leading-tight mb-0.5">{row.productname}</h3>
                                        <span className="text-[10px] font-bold text-blue-600 uppercase bg-blue-50 px-1.5 py-0.5 rounded">{row.store_name}</span>
                                    </div>
                                    <div className="text-right">
                                        <p className="text-blue-700 font-bold text-sm">{formatCurrency(row.total_value)}</p>
                                        <p className="text-[9px] text-gray-400 font-medium">{format(new Date(row.receiptdate), "dd MMM, HH:mm")}</p>
                                    </div>
                                </div>

                                <div className="grid grid-cols-2 gap-2 pt-3 border-t border-gray-100">
                                    <div className="flex flex-col">
                                        <span className="text-[9px] text-gray-400 font-bold uppercase">Ordered</span>
                                        <span className="text-sm font-mono text-gray-500 font-medium">{row.po_qty_ordered || "—"}</span>
                                    </div>
                                    <div className="flex flex-col items-end">
                                        <span className="text-[9px] text-gray-400 font-bold uppercase">Received</span>
                                        <div className="flex items-center gap-1">
                                            <span className={`text-sm font-mono font-bold ${row.is_short_shipment ? 'text-orange-600' : 'text-green-600'}`}>
                                                {row.qtyreceived}
                                            </span>
                                            <span className="text-[10px] text-gray-400 font-bold">{row.quantitymeasure}</span>
                                        </div>
                                    </div>
                                </div>
                                <div className="mt-2 text-[10px] text-gray-400 italic border-t border-gray-50 pt-1">
                                    Vendor: {row.vendorname || "Manual Receipt"}
                                </div>
                            </div>
                        ))}
                    </div>
                </>
            )}
            {/* <TrialModal isOpen={showTrialModal} onRequestClose={() => setShowTrialModal(false)} /> */}
        </div>
    );
};

export default GoodsReceiptHistory;